/**
 * VolunteersDashboard — Admin view for the public volunteer-interest pool.
 * Lists submissions from /api/volunteer-interests, supports status/interest
 * filtering, free-text search, and inline status updates.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './VolunteersDashboard.css';

// ── Types ───────────────────────────────────────────────────────────────────

interface Volunteer {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  interests: string[];
  availability: string | null;
  can_commit_min_hours: boolean;
  has_stem_background: boolean;
  willing_background_check: boolean;
  message: string | null;
  status: string;
  source: string;
  created_at: string;
}

type StatusValue = 'new' | 'contacted' | 'active' | 'archived';

const STATUSES: StatusValue[] = ['new', 'contacted', 'active', 'archived'];

// Keep in sync with backend services/volunteer_service.py INTEREST_LABELS
const INTEREST_LABELS: Record<string, string> = {
  stem_mentor: 'STEM Mentor',
  events_volunteer: 'Events Volunteer',
  operations_assistant: 'Operations Assistant',
  community_program: 'Community & Program',
  marketing_comms: 'Marketing & Comms',
  grant_coordinator: 'Grant Coordinator',
  general: 'General',
};

const INTEREST_FILTERS = Object.keys(INTEREST_LABELS);

// ── Helpers ─────────────────────────────────────────────────────────────────

function interestLabel(key: string): string {
  return INTEREST_LABELS[key] || key;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Vancouver',
  });
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

const VolunteerDetail: React.FC<{
  v: Volunteer;
  onClose: () => void;
  onStatusChange: (id: string, status: StatusValue) => void;
  saving: boolean;
}> = ({ v, onClose, onStatusChange, saving }) => {
  return (
    <div className="vd-modal-overlay" onClick={onClose}>
      <div className="vd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vd-modal-header">
          <div>
            <div className="vd-modal-subtitle">Volunteer Interest</div>
            <h2 className="vd-modal-title">{v.name || 'Unknown'}</h2>
            <div className="vd-modal-meta">
              <span>{v.email}</span>
              {v.phone && <span> · {v.phone}</span>}
              {v.city && <span> · {v.city}</span>}
            </div>
          </div>
          <button className="vd-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="vd-modal-body">
          <div className="vd-qa">
            <div className="vd-question">Areas of interest</div>
            <div className="vd-answer">
              {v.interests?.length ? (
                <div className="vd-badges">
                  {v.interests.map((k) => (
                    <span key={k} className="vd-interest-badge">{interestLabel(k)}</span>
                  ))}
                </div>
              ) : <em className="vd-no-answer">None specified</em>}
            </div>
          </div>

          <div className="vd-qa">
            <div className="vd-question">Availability</div>
            <div className="vd-answer">{v.availability || <em className="vd-no-answer">Not provided</em>}</div>
          </div>

          <div className="vd-qa">
            <div className="vd-question">Commitment & screening</div>
            <div className="vd-answer">
              <div className="vd-flags">
                <span className={`vd-flag ${v.can_commit_min_hours ? 'vd-flag-yes' : 'vd-flag-no'}`}>
                  {v.can_commit_min_hours ? '✓' : '✗'} 10+ hrs / month
                </span>
                <span className={`vd-flag ${v.has_stem_background ? 'vd-flag-yes' : 'vd-flag-no'}`}>
                  {v.has_stem_background ? '✓' : '✗'} STEM background
                </span>
                <span className={`vd-flag ${v.willing_background_check ? 'vd-flag-yes' : 'vd-flag-no'}`}>
                  {v.willing_background_check ? '✓' : '✗'} Open to background check
                </span>
              </div>
            </div>
          </div>

          <div className="vd-qa">
            <div className="vd-question">Message</div>
            <div className="vd-answer vd-answer-message">
              {v.message || <em className="vd-no-answer">No message</em>}
            </div>
          </div>

          <div className="vd-qa">
            <div className="vd-question">Status</div>
            <div className="vd-answer">
              <select
                className="vd-status-select"
                value={v.status}
                disabled={saving}
                onChange={(e) => onStatusChange(v.id, e.target.value as StatusValue)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="vd-meta-section">
            <div className="vd-meta-row"><span>Submitted</span><span>{formatDate(v.created_at)}</span></div>
            <div className="vd-meta-row"><span>Source</span><span>{v.source}</span></div>
            <div className="vd-meta-row"><span>ID</span><code>{v.id}</code></div>
          </div>

          <div className="vd-modal-actions">
            <a className="vd-email-btn" href={`mailto:${v.email}?subject=${encodeURIComponent('Volunteering with OARA')}`}>
              ✉ Email {v.name?.split(' ')[0] || 'volunteer'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const VolunteersDashboard: React.FC = () => {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Volunteer | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const fetchVolunteers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const res = await axios.get(`${config.BACKEND_URL}/api/volunteer-interests`, {
        params: {
          status: statusFilter || undefined,
          interest: interestFilter || undefined,
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setVolunteers(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load volunteers.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, interestFilter]);

  useEffect(() => { fetchVolunteers(); }, [fetchVolunteers]);

  const handleStatusChange = useCallback(async (id: string, status: StatusValue) => {
    setSavingId(id);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const res = await axios.patch(
        `${config.BACKEND_URL}/api/volunteer-interests/${id}`,
        { status },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const updated: Volunteer = res.data;
      setVolunteers((prev) => prev.map((v) => (v.id === id ? updated : v)));
      setSelected((prev) => (prev && prev.id === id ? updated : prev));
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to update status.');
    } finally {
      setSavingId(null);
    }
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return volunteers;
    const q = search.toLowerCase();
    return volunteers.filter((v) => {
      return (
        (v.name || '').toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q) ||
        (v.city || '').toLowerCase().includes(q) ||
        (v.message || '').toLowerCase().includes(q) ||
        (v.interests || []).some((k) => interestLabel(k).toLowerCase().includes(q))
      );
    });
  }, [volunteers, search]);

  const hasServerFilter = !!statusFilter || !!interestFilter;

  return (
    <div className="vd-container">
      {/* Header */}
      <div className="vd-header">
        <div>
          <h2 className="vd-title">🙋 Volunteer Pool</h2>
          <p className="vd-subtitle">
            {loading ? 'Loading…' : `${volunteers.length} volunteer${volunteers.length !== 1 ? 's' : ''}${hasServerFilter ? ' (filtered)' : ''} · stored in database`}
          </p>
        </div>
        <div className="vd-header-actions">
          <select className="vd-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="vd-filter" value={interestFilter} onChange={(e) => setInterestFilter(e.target.value)}>
            <option value="">All interests</option>
            {INTEREST_FILTERS.map((k) => <option key={k} value={k}>{interestLabel(k)}</option>)}
          </select>
          <input
            className="vd-search"
            type="text"
            placeholder="Search name, email, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="vd-refresh-btn" onClick={fetchVolunteers} disabled={loading}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* States */}
      {loading && <div className="vd-state">Loading volunteers…</div>}
      {error   && <div className="vd-state vd-state-error">{error}</div>}

      {!loading && !error && volunteers.length === 0 && (
        <div className="vd-state vd-state-empty">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🤝</div>
          <p>No volunteers in the pool{hasServerFilter ? ' for this filter' : ' yet'}.</p>
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>
            Submissions from <a href="https://oarayouthinnovationlab.ca/careers/" target="_blank" rel="noreferrer">the careers page</a> will appear here.
          </p>
        </div>
      )}

      {!loading && !error && volunteers.length > 0 && (
        <div className="vd-table-wrap">
          {search && (
            <div className="vd-filter-note">
              Showing {filtered.length} of {volunteers.length} · <button className="vd-clear-search" onClick={() => setSearch('')}>Clear search</button>
            </div>
          )}
          <table className="vd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Volunteer</th>
                <th>Interests</th>
                <th>Availability</th>
                <th>Commit</th>
                <th>Submitted</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v, i) => (
                <tr key={v.id} className="vd-row" onClick={() => setSelected(v)}>
                  <td className="vd-num">{filtered.length - i}</td>
                  <td>
                    <div className="vd-cell-main">{v.name || <em>Unknown</em>}</div>
                    <div className="vd-cell-sub">{v.email}</div>
                    {v.phone && <div className="vd-cell-sub">📱 {v.phone}</div>}
                  </td>
                  <td>
                    {v.interests?.length ? (
                      <div className="vd-badges">
                        {v.interests.map((k) => (
                          <span key={k} className="vd-interest-badge">{interestLabel(k)}</span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="vd-cell-compact">{v.availability || '—'}</td>
                  <td className="vd-cell-compact">
                    {v.can_commit_min_hours ? <span className="vd-commit-yes">10+ hrs</span> : '—'}
                  </td>
                  <td className="vd-date">{formatDate(v.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className={`vd-status-select vd-status-${v.status}`}
                      value={v.status}
                      disabled={savingId === v.id}
                      onChange={(e) => handleStatusChange(v.id, e.target.value as StatusValue)}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="vd-view-cell" onClick={(e) => e.stopPropagation()}>
                    <button className="vd-view-btn" onClick={() => setSelected(v)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <VolunteerDetail
          v={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          saving={savingId === selected.id}
        />
      )}
    </div>
  );
};

export default VolunteersDashboard;
